import React, { useState, useEffect, useRef } from 'react';
import { db } from '../../firebase/config';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { X, Printer, Camera } from 'lucide-react';
import { useHiddenStudentRoute } from '../../utils/studentRoutes';
import { getStudentPhotoUrl } from '../../utils/mediaUrls';

function AttendanceRep() {
  const { rollNumber } = useHiddenStudentRoute('/attendance-report');
  const reportRef = useRef();
  
  const [student, setStudent] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLayout, setSelectedLayout] = useState('layout2'); // Default to Layout 2

  useEffect(() => {
    if (!rollNumber) {
      setLoading(false);
      return;
    }
    loadData();
  }, [rollNumber]);

  const fetchMentorAndCoordinator = async (batch, division) => {
    try {
      let mentorName = 'Not Assigned';
      let ccName = 'Not Assigned';

      if (batch) {
        const batchQuery = query(
          collection(db, 'batchAssignments'),
          where('batch', '==', batch)
        );
        const batchSnapshot = await getDocs(batchQuery);
        
        if (!batchSnapshot.empty) {
          const batchData = batchSnapshot.docs[0].data();
          mentorName = batchData.mentorName || 'Not Assigned';
        }
      }

      if (division) {
        const divisionQuery = query(
          collection(db, 'divisionAssignments'),
          where('division', '==', division)
        );
        const divisionSnapshot = await getDocs(divisionQuery);
        
        if (!divisionSnapshot.empty) {
          const divisionData = divisionSnapshot.docs[0].data();
          ccName = divisionData.coordinatorName || 'Not Assigned';
        }
      }

      return { mentorName, ccName };
    } catch (error) {
      console.error('Error fetching mentor/coordinator:', error);
      return { mentorName: 'Error loading', ccName: 'Error loading' };
    }
  };

  const loadData = async () => {
    try {
      const studentDoc = await getDoc(doc(db, 'students', rollNumber));
      if (studentDoc.exists()) {
        const studentData = studentDoc.data();
        
        const { mentorName, ccName } = await fetchMentorAndCoordinator(
          studentData.batch,
          studentData.division
        );
        
        setStudent({
          ...studentData,
          mentorName: mentorName,
          ccName: ccName
        });
      }

      const attendanceDoc = await getDoc(doc(db, 'attendance', rollNumber));
      if (attendanceDoc.exists()) {
        setAttendance(attendanceDoc.data());
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
    }
  };

  const handlePrint = () => window.print();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!student || !attendance) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-xl text-gray-600">Data not found</p>
      </div>
    );
  }

  // Separate theory and practical coursessrc
  const theoryCourses = attendance.courses?.filter(course => 
    course.name.toLowerCase().includes('theory') && 
    !course.name.toLowerCase().includes('tutorial') &&
    !course.name.toLowerCase().includes('lab') &&
    !course.name.toLowerCase().includes('practical')
  ) || [];

  // Add UHV to theory
  const uhvCourse = attendance.courses?.find(course => 
    course.name.toLowerCase().includes('universal human values')
  );
  if (uhvCourse) theoryCourses.push(uhvCourse);

  const practicalCourses = attendance.courses?.filter(course => 
    course.name.toLowerCase().includes('practical') || 
    course.name.toLowerCase().includes('lab') ||
    course.name.toLowerCase().includes('tutorial') ||
    course.name.toLowerCase().includes('workshop')
  ) || [];

  const allCourses = attendance.courses || [];

  // Render different layouts
  const renderLayout = () => {
    switch(selectedLayout) {
      case 'layout1':
        return <Layout1 courses={allCourses} attendance={attendance} />;
      case 'layout2':
        return <Layout2 theoryCourses={theoryCourses} practicalCourses={practicalCourses} attendance={attendance} />;
      case 'layout3':
        return <Layout3 courses={allCourses} attendance={attendance} />;
      case 'layout4':
        return <Layout4 theoryCourses={theoryCourses} practicalCourses={practicalCourses} attendance={attendance} />;
      default:
        return <Layout2 theoryCourses={theoryCourses} practicalCourses={practicalCourses} attendance={attendance} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-4 print:bg-white print:py-0">
      <div className="max-w-[210mm] mx-auto px-4 print:px-0">
        
        {/* Action Buttons */}
        <div className="flex justify-between items-center mb-4 print:hidden">
          <button 
            onClick={() => window.close()} 
            className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            <X className="w-4 h-4" />
            <span>Close</span>
          </button>

          {/* Layout Selector */}
          <div className="flex items-center space-x-3">
            <label className="text-sm font-semibold text-gray-700">Report Layout:</label>
            <select 
              value={selectedLayout}
              onChange={(e) => setSelectedLayout(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="layout1">Layout 1 - Simple Linear</option>
              <option value="layout2">Layout 2 - Grouped Sections ⭐</option>
              <option value="layout3">Layout 3 - Compact with Codes</option>
              <option value="layout4">Layout 4 - Modern Card Style</option>
            </select>
          </div>

          <button 
            onClick={handlePrint} 
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Printer className="w-4 h-4" />
            <span>Print Report</span>
          </button>
        </div>

        {/* A4 Report */}
        <div 
          ref={reportRef}
          className="bg-white shadow-lg print:shadow-none"
          style={{
            width: '210mm',
            minHeight: '297mm',
            padding: '12mm',
            margin: '0 auto'
          }}
        >
          {/* Letterhead */}
          <div className="mb-3">
            <img 
              src="/ybit-letterhead.png" 
              alt="YBIT Letterhead" 
              className="w-full h-auto object-contain"
              style={{ maxHeight: '80px' }}
            />
          </div>
          
          <div className="border-b-2 border-black mb-3"></div>

          {/* Report Title */}
          <h2 className="text-lg font-bold text-center mb-4 uppercase">Attendance Report - Semester I</h2>

          {/* Student Info with Photo */}
          <div className="grid grid-cols-4 gap-4 mb-4 pb-3 border-b border-gray-300">
            <div className="col-span-3">
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <div>
                  <span className="font-semibold">Name:</span> {student.firstName} {student.surname}
                </div>
                <div>
                  <span className="font-semibold">Roll Number:</span> {rollNumber}
                </div>
                <div>
                  <span className="font-semibold">Division:</span> {student.division}
                </div>
                <div>
                  <span className="font-semibold">Batch:</span> {student.batch}
                </div>
                <div>
                  <span className="font-semibold">Mentor:</span> {student.mentorName || 'Not Assigned'}
                </div>
                <div>
                  <span className="font-semibold">Class Coordinator:</span> {student.ccName || 'Not Assigned'}
                </div>
              </div>
            </div>
            
            {/* Student Photo */}
            <div className="col-span-1 flex justify-center">
              <div className="w-24 h-24 border-2 border-gray-300 rounded overflow-hidden bg-gray-100">
                {getStudentPhotoUrl(student) ? (
                  <img 
                    src={getStudentPhotoUrl(student)}  
                    alt="Student" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Camera className="w-8 h-8 text-gray-400" />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Render Selected Layout */}
          {renderLayout()}

          {/* Remarks */}
          <div className="bg-blue-50 border-l-4 border-blue-600 p-3 mb-4 mt-4">
            <h3 className="font-bold text-blue-900 mb-1 text-sm">📝 Attendance Remarks</h3>
            <p className="text-sm">
              {attendance.overallPercentage >= 95 ? (
                <span className="text-blue-800">🌟 <strong>Excellent attendance: {Number(attendance.overallPercentage).toFixed(2)}%.</strong> Keep it up!</span>
              ) : attendance.overallPercentage >= 85 ? (
                <span className="text-blue-800">👏 <strong>Good attendance: {Number(attendance.overallPercentage).toFixed(2)}%.</strong> Maintain consistency!</span>
              ) : attendance.overallPercentage >= 75 ? (
                <span className="text-yellow-800">⚠️ <strong>Satisfactory attendance: {Number(attendance.overallPercentage).toFixed(2)}%.</strong> Try to improve.</span>
              ) : (
                <span className="text-red-800">❌ <strong>Below required attendance: {Number(attendance.overallPercentage).toFixed(2)}%.</strong> Urgent improvement needed! Minimum 75% required.</span>
              )}
            </p>
          </div>

          {/* Signature Section */}
          <div className="mt-8 pt-6 border-t-2 border-gray-300">
            <div className="grid grid-cols-3 gap-8 text-center text-sm">
              <div>
                <div className="border-t-2 border-black w-full mb-1 mt-12"></div>
                <p className="font-semibold">Mentor's Sign</p>
              </div>
              <div>
                <div className="border-t-2 border-black w-full mb-1 mt-12"></div>
                <p className="font-semibold">Class Coordinator's Sign</p>
               
              </div>
              <div>
                <div className="border-t-2 border-black w-full mb-1 mt-12"></div>
                <p className="font-semibold">HOD's Sign</p>
              
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center text-[9px] text-gray-600 mt-6 pt-3 border-t border-gray-300">
            <p>Bhonsale Knowledge City, Building No.1, Vazarwadi, Charathe, Tal: Sawantwadi, Dist: Sindhudurg 416510</p>
            <p>DTE CODE: 3470 | MSBTE CODE: 1742 | Generated on: {new Date().toLocaleDateString()}</p>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          body { margin: 0; padding: 0; }
          .print\\:hidden { display: none !important; }
          .print\\:bg-white { background: white !important; }
          .print\\:py-0 { padding-top: 0 !important; padding-bottom: 0 !important; }
          .print\\:px-0 { padding-left: 0 !important; padding-right: 0 !important; }
          .print\\:shadow-none { box-shadow: none !important; }
          @page { size: A4; margin: 0; }
        }
      `}</style>
    </div>
  );
}

// Layout 1: Simple Linear Table
function Layout1({ courses, attendance }) {
  return (
    <table className="w-full border-collapse mb-4 text-sm">
      <thead>
        <tr className="bg-blue-600 text-white">
          <th className="border border-gray-300 px-2 py-2 text-left">Course Name</th>
          <th className="border border-gray-300 px-2 py-2 text-center w-24">Conducted</th>
          <th className="border border-gray-300 px-2 py-2 text-center w-24">Attended</th>
        </tr>
      </thead>
      <tbody>
        {courses.map((course, index) => (
          <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
            <td className="border border-gray-300 px-2 py-1.5">{course.name}</td>
            <td className="border border-gray-300 px-2 py-1.5 text-center">{course.conducted}</td>
            <td className="border border-gray-300 px-2 py-1.5 text-center">{course.attended}</td>
          </tr>
        ))}
        <tr className="bg-blue-100 font-semibold">
          <td className="border border-gray-300 px-2 py-1.5">THEORY ATTENDANCE TOTAL</td>
          <td className="border border-gray-300 px-2 py-1.5 text-center">{attendance.theoryTotal?.conducted || 0}</td>
          <td className="border border-gray-300 px-2 py-1.5 text-center">{attendance.theoryTotal?.attended || 0}</td>
        </tr>
        <tr className="bg-blue-200 font-bold">
          <td className="border border-gray-300 px-2 py-1.5">THEORY ATTENDANCE PERCENTAGE</td>
          <td className="border border-gray-300 px-2 py-1.5 text-center" colSpan="2">
            {Number(attendance.theoryPercentage).toFixed(2)}%
          </td>
        </tr>
        <tr className="bg-blue-100 font-semibold">
          <td className="border border-gray-300 px-2 py-1.5">PRACTICAL ATTENDANCE TOTAL</td>
          <td className="border border-gray-300 px-2 py-1.5 text-center">{attendance.practicalTotal?.conducted || 0}</td>
          <td className="border border-gray-300 px-2 py-1.5 text-center">{attendance.practicalTotal?.attended || 0}</td>
        </tr>
        <tr className="bg-blue-200 font-bold">
          <td className="border border-gray-300 px-2 py-1.5">PRACTICAL ATTENDANCE PERCENTAGE</td>
          <td className="border border-gray-300 px-2 py-1.5 text-center" colSpan="2">
            {Number(attendance.practicalPercentage).toFixed(2)}%
          </td>
        </tr>
        <tr className="bg-blue-600 text-white font-bold">
          <td className="border border-gray-300 px-2 py-2">OVERALL ATTENDANCE PERCENTAGE</td>
          <td className="border border-gray-300 px-2 py-2 text-center" colSpan="2">
            {Number(attendance.overallPercentage).toFixed(2)}%
          </td>
        </tr>
      </tbody>
    </table>
  );
}

// Layout 2: Grouped Sections (Recommended)
function Layout2({ theoryCourses, practicalCourses, attendance }) {
  return (
    <div className="mb-4">
      {/* Theory Section */}
      <div className="mb-4">
        <h3 className="bg-blue-600 text-white px-2 py-1.5 font-bold text-sm mb-2">THEORY SUBJECTS</h3>
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-gray-200">
              <th className="border border-gray-300 px-2 py-1.5 text-left">Course Name</th>
              <th className="border border-gray-300 px-2 py-1.5 text-center w-24">Conducted</th>
              <th className="border border-gray-300 px-2 py-1.5 text-center w-24">Attended</th>
            </tr>
          </thead>
          <tbody>
            {theoryCourses.map((course, index) => (
              <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                <td className="border border-gray-300 px-2 py-1.5">{course.name}</td>
                <td className="border border-gray-300 px-2 py-1.5 text-center">{course.conducted}</td>
                <td className="border border-gray-300 px-2 py-1.5 text-center">{course.attended}</td>
              </tr>
            ))}
            <tr className="bg-blue-100 font-semibold">
              <td className="border border-gray-300 px-2 py-1.5">THEORY TOTAL</td>
              <td className="border border-gray-300 px-2 py-1.5 text-center">{attendance.theoryTotal?.conducted || 0}</td>
              <td className="border border-gray-300 px-2 py-1.5 text-center">{attendance.theoryTotal?.attended || 0}</td>
            </tr>
            <tr className="bg-blue-200 font-bold">
              <td className="border border-gray-300 px-2 py-1.5">THEORY PERCENTAGE</td>
              <td className="border border-gray-300 px-2 py-1.5 text-center" colSpan="2">
                {Number(attendance.theoryPercentage).toFixed(2)}%
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Practical Section */}
      <div className="mb-4">
        <h3 className="bg-green-600 text-white px-2 py-1.5 font-bold text-sm mb-2">PRACTICAL SUBJECTS</h3>
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-gray-200">
              <th className="border border-gray-300 px-2 py-1.5 text-left">Course Name</th>
              <th className="border border-gray-300 px-2 py-1.5 text-center w-24">Conducted</th>
              <th className="border border-gray-300 px-2 py-1.5 text-center w-24">Attended</th>
            </tr>
          </thead>
          <tbody>
            {practicalCourses.map((course, index) => (
              <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                <td className="border border-gray-300 px-2 py-1.5">{course.name}</td>
                <td className="border border-gray-300 px-2 py-1.5 text-center">{course.conducted}</td>
                <td className="border border-gray-300 px-2 py-1.5 text-center">{course.attended}</td>
              </tr>
            ))}
            <tr className="bg-green-100 font-semibold">
              <td className="border border-gray-300 px-2 py-1.5">PRACTICAL TOTAL</td>
              <td className="border border-gray-300 px-2 py-1.5 text-center">{attendance.practicalTotal?.conducted || 0}</td>
              <td className="border border-gray-300 px-2 py-1.5 text-center">{attendance.practicalTotal?.attended || 0}</td>
            </tr>
            <tr className="bg-green-200 font-bold">
              <td className="border border-gray-300 px-2 py-1.5">PRACTICAL PERCENTAGE</td>
              <td className="border border-gray-300 px-2 py-1.5 text-center" colSpan="2">
                {Number(attendance.practicalPercentage).toFixed(2)}%
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Overall */}
      <div className="bg-blue-600 text-white px-3 py-2 rounded font-bold text-center">
        OVERALL ATTENDANCE PERCENTAGE: {Number(attendance.overallPercentage).toFixed(2)}%
      </div>
    </div>
  );
}

// Layout 3: Compact with Subject Codes
function Layout3({ courses, attendance }) {
  const getSubjectCode = (name) => {
    if (name.includes('Applied Mathematics')) return 'AM-I';
    if (name.includes('Applied Physics')) return 'AP';
    if (name.includes('Applied Chemistry')) return 'AC';
    if (name.includes('Engineering Mechanics')) return 'EM';
    if (name.includes('Basic Electrical')) return 'BEE';
    if (name.includes('Professional Communication')) return 'PCE';
    if (name.includes('C Programming')) return 'CP';
    if (name.includes('Universal Human')) return 'UHV';
    if (name.includes('Engineering Workshop')) return 'EW-1';
    return 'N/A';
  };

  const getType = (name) => {
    if (name.includes('Theory')) return '(T)';
    if (name.includes('Tutorial')) return '(Tut)';
    if (name.includes('Practical')) return '(P)';
    if (name.includes('Lab')) return '(L)';
    return '';
  };

  return (
    <table className="w-full border-collapse mb-4 text-xs">
      <thead>
        <tr className="bg-blue-600 text-white">
          <th className="border border-gray-300 px-2 py-1.5 text-left w-20">Code</th>
          <th className="border border-gray-300 px-2 py-1.5 text-left">Subject Name</th>
          <th className="border border-gray-300 px-2 py-1.5 text-center w-16">Cond</th>
          <th className="border border-gray-300 px-2 py-1.5 text-center w-16">Att</th>
          <th className="border border-gray-300 px-2 py-1.5 text-center w-16">%</th>
        </tr>
      </thead>
      <tbody>
        {courses.map((course, index) => {
          const percentage = course.conducted > 0 ? ((course.attended / course.conducted) * 100).toFixed(0) : 0;
          return (
            <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
              <td className="border border-gray-300 px-2 py-1">
                {getSubjectCode(course.name)} {getType(course.name)}
              </td>
              <td className="border border-gray-300 px-2 py-1">{course.name}</td>
              <td className="border border-gray-300 px-2 py-1 text-center">{course.conducted}</td>
              <td className="border border-gray-300 px-2 py-1 text-center font-semibold">{course.attended}</td>
              <td className="border border-gray-300 px-2 py-1 text-center">{percentage}</td>
            </tr>
          );
        })}
        <tr className="bg-blue-100 font-bold">
          <td className="border border-gray-300 px-2 py-1.5" colSpan="2">THEORY TOTAL</td>
          <td className="border border-gray-300 px-2 py-1.5 text-center">{attendance.theoryTotal?.conducted || 0}</td>
          <td className="border border-gray-300 px-2 py-1.5 text-center">{attendance.theoryTotal?.attended || 0}</td>
          <td className="border border-gray-300 px-2 py-1.5 text-center">{Number(attendance.theoryPercentage).toFixed(0)}</td>
        </tr>
        <tr className="bg-green-100 font-bold">
          <td className="border border-gray-300 px-2 py-1.5" colSpan="2">PRACTICAL TOTAL</td>
          <td className="border border-gray-300 px-2 py-1.5 text-center">{attendance.practicalTotal?.conducted || 0}</td>
          <td className="border border-gray-300 px-2 py-1.5 text-center">{attendance.practicalTotal?.attended || 0}</td>
          <td className="border border-gray-300 px-2 py-1.5 text-center">{Number(attendance.practicalPercentage).toFixed(0)}</td>
        </tr>
        <tr className="bg-blue-600 text-white font-bold">
          <td className="border border-gray-300 px-2 py-2" colSpan="2">OVERALL TOTAL</td>
          <td className="border border-gray-300 px-2 py-2 text-center">
            {(attendance.theoryTotal?.conducted || 0) + (attendance.practicalTotal?.conducted || 0)}
          </td>
          <td className="border border-gray-300 px-2 py-2 text-center">
            {(attendance.theoryTotal?.attended || 0) + (attendance.practicalTotal?.attended || 0)}
          </td>
          <td className="border border-gray-300 px-2 py-2 text-center">{Number(attendance.overallPercentage).toFixed(0)}</td>
        </tr>
      </tbody>
    </table>
  );
}

// Layout 4: Modern Card Style
function Layout4({ theoryCourses, practicalCourses, attendance }) {
  return (
    <div className="mb-4 space-y-4">
      {/* Theory Card */}
      <div className="border-2 border-blue-600 rounded-lg overflow-hidden">
        <div className="bg-blue-600 text-white px-3 py-2 flex justify-between items-center">
          <span className="font-bold">📚 THEORY ATTENDANCE</span>
          <span className="text-xl font-bold">{Number(attendance.theoryPercentage).toFixed(2)}%</span>
        </div>
        <div className="p-3 bg-blue-50 text-sm">
          <p><strong>Conducted:</strong> {attendance.theoryTotal?.conducted || 0} | <strong>Attended:</strong> {attendance.theoryTotal?.attended || 0} | <strong>Missed:</strong> {(attendance.theoryTotal?.conducted || 0) - (attendance.theoryTotal?.attended || 0)}</p>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-200">
            <tr>
              <th className="border border-gray-300 px-2 py-1 text-left">Subject</th>
              <th className="border border-gray-300 px-2 py-1 text-center w-20">Cond</th>
              <th className="border border-gray-300 px-2 py-1 text-center w-20">Att</th>
              <th className="border border-gray-300 px-2 py-1 text-center w-12">✓</th>
            </tr>
          </thead>
          <tbody>
            {theoryCourses.map((course, index) => (
              <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="border border-gray-300 px-2 py-1">{course.name}</td>
                <td className="border border-gray-300 px-2 py-1 text-center">{course.conducted}</td>
                <td className="border border-gray-300 px-2 py-1 text-center font-semibold">{course.attended}</td>
                <td className="border border-gray-300 px-2 py-1 text-center">
                  {course.attended === course.conducted ? '✓' : '⚠'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Practical Card */}
      <div className="border-2 border-green-600 rounded-lg overflow-hidden">
        <div className="bg-green-600 text-white px-3 py-2 flex justify-between items-center">
          <span className="font-bold">🔬 PRACTICAL ATTENDANCE</span>
          <span className="text-xl font-bold">{Number(attendance.practicalPercentage).toFixed(2)}%</span>
        </div>
        <div className="p-3 bg-green-50 text-sm">
          <p><strong>Conducted:</strong> {attendance.practicalTotal?.conducted || 0} | <strong>Attended:</strong> {attendance.practicalTotal?.attended || 0} | <strong>Missed:</strong> {(attendance.practicalTotal?.conducted || 0) - (attendance.practicalTotal?.attended || 0)}</p>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-200">
            <tr>
              <th className="border border-gray-300 px-2 py-1 text-left">Subject</th>
              <th className="border border-gray-300 px-2 py-1 text-center w-20">Cond</th>
              <th className="border border-gray-300 px-2 py-1 text-center w-20">Att</th>
              <th className="border border-gray-300 px-2 py-1 text-center w-12">✓</th>
            </tr>
          </thead>
          <tbody>
            {practicalCourses.map((course, index) => (
              <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="border border-gray-300 px-2 py-1">{course.name}</td>
                <td className="border border-gray-300 px-2 py-1 text-center">{course.conducted}</td>
                <td className="border border-gray-300 px-2 py-1 text-center font-semibold">{course.attended}</td>
                <td className="border border-gray-300 px-2 py-1 text-center">
                  {course.attended === course.conducted ? '✓' : '⚠'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Overall Summary */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-3 rounded-lg text-center">
        <p className="text-2xl font-bold">🎯 OVERALL ATTENDANCE: {Number(attendance.overallPercentage).toFixed(2)}%</p>
        <p className="text-sm mt-1">
          ({(attendance.theoryTotal?.attended || 0) + (attendance.practicalTotal?.attended || 0)} / 
          {(attendance.theoryTotal?.conducted || 0) + (attendance.practicalTotal?.conducted || 0)})
        </p>
      </div>
    </div>
  );
}

export default AttendanceRep;
